import { createIcon } from './Icon';
import ArrowsCounterClockwiseSvg from './svg/ArrowsCounterClockwise.svg?react';
import ArrowUpSvg from './svg/ArrowUp.svg?react';
import BookOpenTextSvg from './svg/BookOpenText.svg?react';
import CalendarBlankSvg from './svg/CalendarBlank.svg?react';
import CaretDownSvg from './svg/CaretDown.svg?react';
import CaretLeftSvg from './svg/CaretLeft.svg?react';
import CaretRightSvg from './svg/CaretRight.svg?react';
import CaretUpSvg from './svg/CaretUp.svg?react';
import ChatCircleSvg from './svg/ChatCircle.svg?react';
import ChatsCircleSvg from './svg/ChatsCircle.svg?react';
import CheckCircleSvg from './svg/CheckCircle.svg?react';
import ChecksSvg from './svg/Checks.svg?react';
import CircleDashedSvg from './svg/CircleDashed.svg?react';
import CircleNotchSvg from './svg/CircleNotch.svg?react';
import CircleProgressSvg from './svg/CircleProgress.svg?react';
import ClosedCaptioningSvg from './svg/ClosedCaptioning.svg?react';
import ConfettiSvg from './svg/Confetti.svg?react';
import CopySvg from './svg/Copy.svg?react';
import DotsThreeSvg from './svg/DotsThree.svg?react';
import DownloadSimpleSvg from './svg/DownloadSimple.svg?react';
import EyeSvg from './svg/Eye.svg?react';
import EyeSlashSvg from './svg/EyeSlash.svg?react';
import FileAudioSvg from './svg/FileAudio.svg?react';
import GearSvg from './svg/Gear.svg?react';
import HandSvg from './svg/Hand.svg?react';
import InfoSvg from './svg/Info.svg?react';
import LinkSvg from './svg/Link.svg?react';
import MagnifyingGlassSvg from './svg/MagnifyingGlass.svg?react';
import MicrophoneSvg from './svg/Microphone.svg?react';
import MicrophoneSlashSvg from './svg/MicrophoneSlash.svg?react';
import MonitorArrowUpSvg from './svg/MonitorArrowUp.svg?react';
import PauseSvg from './svg/Pause.svg?react';
import PencilSimpleSvg from './svg/PencilSimple.svg?react';
import PhoneDisconnectSvg from './svg/PhoneDisconnect.svg?react';
import PlaySvg from './svg/Play.svg?react';
import PlusSvg from './svg/Plus.svg?react';
import QuestionSvg from './svg/Question.svg?react';
import RecordSvg from './svg/Record.svg?react';
import SignOutSvg from './svg/SignOut.svg?react';
import SpeakerSimpleHighSvg from './svg/SpeakerSimpleHigh.svg?react';
import SpeakerSimpleSlashSvg from './svg/SpeakerSimpleSlash.svg?react';
import ThumbsDownSvg from './svg/ThumbsDown.svg?react';
import ThumbsUpSvg from './svg/ThumbsUp.svg?react';
import UploadSimpleSvg from './svg/UploadSimple.svg?react';
import UsersSvg from './svg/Users.svg?react';
import VideoCameraSvg from './svg/VideoCamera.svg?react';
import VideoCameraSlashSvg from './svg/VideoCameraSlash.svg?react';
import WarningSvg from './svg/Warning.svg?react';
import WarningCircleSvg from './svg/WarningCircle.svg?react';
import WaveformSvg from './svg/Waveform.svg?react';
import WaveformActiveSvg from './svg/WaveformActive.svg?react';
import XSvg from './svg/X.svg?react';

/**
 * The Design System 2.0 icon set, one named export per glyph.
 *
 * Named exports only — tree-shakeable, and the idiom `src/icons` and `Dialog.tsx` already use.
 * There is deliberately no `<Icon name="…" />` prop API: nothing in the app resolves an icon by
 * string at runtime (`Alert`'s severity map works by putting named imports in the map), and a name
 * API would trade away tree-shaking for a consumer that does not exist. Add one when something
 * genuinely needs it, and price the loss then.
 *
 * The SOURCE of a glyph is an implementation detail. 48 of these are synthesised from Phosphor by
 * `scripts/generate-icons.mjs`; `DotsThreeIcon` and `WaveformActiveIcon` are Natter-drawn. Nothing
 * at a call site can tell, which is the point — an icon can change source without touching a
 * consumer.
 *
 * Every export carries a `#__PURE__` annotation. Rollup currently tree-shakes the unused glyphs
 * without it — verified: `strokeWidth="3.2"` appears in no built chunk — but that rests on its own
 * side-effect analysis of a factory call re-exported through a barrel, which is a heuristic rather
 * than a guarantee. The annotation states the fact outright, so 50 icons cannot quietly start
 * riding into the main chunk on a bundler upgrade. A test asserts every export still has one.
 *
 * Hand-maintained alongside `icon-manifest.ts`, and the tests in `__tests__/icons.test.ts` assert
 * both directions: every manifest row has an SVG and an export here, and every SVG and every
 * export has a manifest row.
 *
 * FOUR NAMES COLLIDE with the legacy `icons` barrel: `ArrowUpIcon`, `CopyIcon`, `LinkIcon` and
 * `PauseIcon`. The barrels are separate modules (`from 'icons'` vs `from 'components'`), so this is
 * not a build break — but the legacy glyphs are Material-derived and look different, so a
 * mis-import is silent. In a file that imports both, alias:
 * `import { ArrowUpIcon as LegacyArrowUpIcon } from 'icons'`. See README.md.
 */

export const ArrowUpIcon = /*#__PURE__*/ createIcon(ArrowUpSvg, 'ArrowUpIcon');
export const ArrowsCounterClockwiseIcon = /*#__PURE__*/ createIcon(
  ArrowsCounterClockwiseSvg,
  'ArrowsCounterClockwiseIcon'
);
export const BookOpenTextIcon = /*#__PURE__*/ createIcon(BookOpenTextSvg, 'BookOpenTextIcon');
export const CalendarBlankIcon = /*#__PURE__*/ createIcon(CalendarBlankSvg, 'CalendarBlankIcon');
export const CaretDownIcon = /*#__PURE__*/ createIcon(CaretDownSvg, 'CaretDownIcon');
export const CaretLeftIcon = /*#__PURE__*/ createIcon(CaretLeftSvg, 'CaretLeftIcon');
export const CaretRightIcon = /*#__PURE__*/ createIcon(CaretRightSvg, 'CaretRightIcon');
export const CaretUpIcon = /*#__PURE__*/ createIcon(CaretUpSvg, 'CaretUpIcon');
export const ChatCircleIcon = /*#__PURE__*/ createIcon(ChatCircleSvg, 'ChatCircleIcon');
export const ChatsCircleIcon = /*#__PURE__*/ createIcon(ChatsCircleSvg, 'ChatsCircleIcon');
export const CheckCircleIcon = /*#__PURE__*/ createIcon(CheckCircleSvg, 'CheckCircleIcon');
export const ChecksIcon = /*#__PURE__*/ createIcon(ChecksSvg, 'ChecksIcon');
export const CircleDashedIcon = /*#__PURE__*/ createIcon(CircleDashedSvg, 'CircleDashedIcon');
export const CircleNotchIcon = /*#__PURE__*/ createIcon(CircleNotchSvg, 'CircleNotchIcon');
export const CircleProgressIcon = /*#__PURE__*/ createIcon(CircleProgressSvg, 'CircleProgressIcon');
export const ClosedCaptioningIcon = /*#__PURE__*/ createIcon(ClosedCaptioningSvg, 'ClosedCaptioningIcon');
export const ConfettiIcon = /*#__PURE__*/ createIcon(ConfettiSvg, 'ConfettiIcon');
export const CopyIcon = /*#__PURE__*/ createIcon(CopySvg, 'CopyIcon');
export const DotsThreeIcon = /*#__PURE__*/ createIcon(DotsThreeSvg, 'DotsThreeIcon');
export const DownloadSimpleIcon = /*#__PURE__*/ createIcon(DownloadSimpleSvg, 'DownloadSimpleIcon');
export const EyeIcon = /*#__PURE__*/ createIcon(EyeSvg, 'EyeIcon');
export const EyeSlashIcon = /*#__PURE__*/ createIcon(EyeSlashSvg, 'EyeSlashIcon');
export const FileAudioIcon = /*#__PURE__*/ createIcon(FileAudioSvg, 'FileAudioIcon');
export const GearIcon = /*#__PURE__*/ createIcon(GearSvg, 'GearIcon');
export const HandIcon = /*#__PURE__*/ createIcon(HandSvg, 'HandIcon');
export const InfoIcon = /*#__PURE__*/ createIcon(InfoSvg, 'InfoIcon');
export const LinkIcon = /*#__PURE__*/ createIcon(LinkSvg, 'LinkIcon');
export const MagnifyingGlassIcon = /*#__PURE__*/ createIcon(MagnifyingGlassSvg, 'MagnifyingGlassIcon');
export const MicrophoneIcon = /*#__PURE__*/ createIcon(MicrophoneSvg, 'MicrophoneIcon');
export const MicrophoneSlashIcon = /*#__PURE__*/ createIcon(MicrophoneSlashSvg, 'MicrophoneSlashIcon');
export const MonitorArrowUpIcon = /*#__PURE__*/ createIcon(MonitorArrowUpSvg, 'MonitorArrowUpIcon');
export const PauseIcon = /*#__PURE__*/ createIcon(PauseSvg, 'PauseIcon');
export const PencilSimpleIcon = /*#__PURE__*/ createIcon(PencilSimpleSvg, 'PencilSimpleIcon');
export const PhoneDisconnectIcon = /*#__PURE__*/ createIcon(PhoneDisconnectSvg, 'PhoneDisconnectIcon');
export const PlayIcon = /*#__PURE__*/ createIcon(PlaySvg, 'PlayIcon');
export const PlusIcon = /*#__PURE__*/ createIcon(PlusSvg, 'PlusIcon');
export const QuestionIcon = /*#__PURE__*/ createIcon(QuestionSvg, 'QuestionIcon');
export const RecordIcon = /*#__PURE__*/ createIcon(RecordSvg, 'RecordIcon');
export const SignOutIcon = /*#__PURE__*/ createIcon(SignOutSvg, 'SignOutIcon');
export const SpeakerSimpleHighIcon = /*#__PURE__*/ createIcon(SpeakerSimpleHighSvg, 'SpeakerSimpleHighIcon');
export const SpeakerSimpleSlashIcon = /*#__PURE__*/ createIcon(SpeakerSimpleSlashSvg, 'SpeakerSimpleSlashIcon');
export const ThumbsDownIcon = /*#__PURE__*/ createIcon(ThumbsDownSvg, 'ThumbsDownIcon');
export const ThumbsUpIcon = /*#__PURE__*/ createIcon(ThumbsUpSvg, 'ThumbsUpIcon');
export const UploadSimpleIcon = /*#__PURE__*/ createIcon(UploadSimpleSvg, 'UploadSimpleIcon');
export const UsersIcon = /*#__PURE__*/ createIcon(UsersSvg, 'UsersIcon');
export const VideoCameraIcon = /*#__PURE__*/ createIcon(VideoCameraSvg, 'VideoCameraIcon');
export const VideoCameraSlashIcon = /*#__PURE__*/ createIcon(VideoCameraSlashSvg, 'VideoCameraSlashIcon');
export const WarningIcon = /*#__PURE__*/ createIcon(WarningSvg, 'WarningIcon');
export const WarningCircleIcon = /*#__PURE__*/ createIcon(WarningCircleSvg, 'WarningCircleIcon');
export const WaveformIcon = /*#__PURE__*/ createIcon(WaveformSvg, 'WaveformIcon');
export const WaveformActiveIcon = /*#__PURE__*/ createIcon(WaveformActiveSvg, 'WaveformActiveIcon');
export const XIcon = /*#__PURE__*/ createIcon(XSvg, 'XIcon');
